import React, { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@apollo/client';
import { GET_PICKUP_DRIVERS_BY_WAREHOUSE_ID } from '../../pickup-task/graphql/queries/getPickupDriversByWarehouseId';
import { useVehicleOptions } from '../../../../hooks/useVehicleOptions';
import { useWarehouseContext } from "../../../../contexts/WarehouseContext";

interface Driver {
    accountId: string;
    driverName: string;
    status: number;
    vehicleId?: number | null;
}

interface Vehicle {
    value: number;
    label: string;
    id: number;
    vehicleNumber: string;
    licensePlate: string;
    status: number;
    vehicleModelId: number;
    vehicleModelName: string;
    vehicleTypeName?: string;
}

interface PickupDataContextType {
    drivers: Driver[];
    vehicles: Vehicle[];
    driversLoading: boolean;
    vehiclesLoading: boolean;
    warehouseId: string | null;
}

const PickupDataContext = createContext<PickupDataContextType | undefined>(undefined);

interface PickupDataProviderProps {
    children: ReactNode;
}

export const PickupDataProvider: React.FC<PickupDataProviderProps> = ({ children }) => {
    const { currentWarehouse } = useWarehouseContext();
    const warehouseId = currentWarehouse?.id!;
    
    // 查询司机数据
    const { data: driverData, loading: driversLoading } = useQuery(GET_PICKUP_DRIVERS_BY_WAREHOUSE_ID, {
        variables: { warehouseId }, // 不传递accountStatus，查询所有司机
        skip: !warehouseId,
    });
    
    // 查询车辆数据
    const { vehicleOptions, vehicles: rawVehicles, loading: vehiclesLoading } = useVehicleOptions(warehouseId ? Number(warehouseId) : undefined);
    
    const drivers = driverData?.getPickupDriversByWarehouseId || [];
    // 使用完整的车辆数据，而不仅仅是选项
    const vehicles = rawVehicles?.map(vehicle => ({
        value: vehicle.id,
        label: `${vehicle.vehicleNumber}-${vehicle.licensePlate}`,
        id: vehicle.id,
        vehicleNumber: vehicle.vehicleNumber,
        licensePlate: vehicle.licensePlate,
        status: vehicle.status,
        vehicleModelId: vehicle.vehicleModelId,
        vehicleModelName: vehicle.vehicleModelName,
        vehicleTypeName: vehicle.vehicleTypeName
    })) || [];
    
    const value: PickupDataContextType = {
        drivers,
        vehicles,
        driversLoading,
        vehiclesLoading,
        warehouseId
    };
    
    return (
        <PickupDataContext.Provider value={value}>
            {children}
        </PickupDataContext.Provider>
    );
};

export const usePickupData = (): PickupDataContextType => {
    const context = useContext(PickupDataContext);
    if (context === undefined) {
        throw new Error('usePickupData must be used within a PickupDataProvider');
    }
    return context;
};